import React, { useCallback, useEffect, useRef } from 'react';
const candidateSelectors = [
    'input',
    'select',
    'textarea',
    'a[href]',
    'button:not([disabled])',
    '[tabindex]',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    'details>summary:first-of-type',
    'details',
    'iframe'
];
/**
 * Handles tabbing past last element or before first element from within the TabTrap
 */
const handleTabWrap = ({ e, refFirstFocusable, refLastFocusable, rootElm }) => {
    if (refFirstFocusable.current) {
        if (rootElm.activeElement === refLastFocusable.current &&
            e.key === 'Tab' &&
            !e.shiftKey) {
            e.preventDefault();
            refFirstFocusable.current.focus();
        }
        if (rootElm.activeElement === refFirstFocusable.current &&
            e.key === 'Tab' &&
            e.shiftKey) {
            e.preventDefault();
            refLastFocusable.current.focus();
        }
    }
};
export const tabbableElementsSelector = candidateSelectors.join(',');
// Disabling because these useEffects are tightly coupled to component scope
// eslint-disable-next-line max-lines-per-function
const TabTrap = ({ children }) => {
    var _a;
    const TIMEOUT_DURATION = 0;
    const root = (_a = document.querySelector('#root')) === null || _a === void 0 ? void 0 : _a.querySelector('div');
    const shadow = root === null || root === void 0 ? void 0 : root.shadowRoot;
    const rootElm = shadow || document;
    const refOuter = useRef(null);
    const refFirstFocusable = useRef(null);
    const refLastFocusable = useRef(null);
    const updateFocusableNodes = useCallback(() => {
        const tabbableNodes = (refOuter.current &&
            refOuter.current.querySelectorAll(tabbableElementsSelector)) ||
            [];
        if (tabbableNodes.length > 0) {
            const filterNode = [...tabbableNodes].filter(element => element.offsetWidth > 0 &&
                element.offsetHeight > 0 &&
                element.tabIndex > -1);
            const [first] = filterNode;
            refFirstFocusable.current = first;
            refLastFocusable.current = tabbableNodes[tabbableNodes.length - 1];
        }
    }, []);
    const onKeyDown = (e) => {
        updateFocusableNodes();
        handleTabWrap({
            e,
            refFirstFocusable,
            refLastFocusable,
            rootElm
        });
    };
    useEffect(() => {
        if (refOuter.current) {
            setTimeout(() => {
                updateFocusableNodes();
                if (refFirstFocusable.current) {
                    // initial focus on modal load
                    refFirstFocusable.current.focus();
                }
            }, TIMEOUT_DURATION);
        }
        return () => {
            rootElm.removeEventListener('keydown', onKeyDown, true);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    rootElm.addEventListener('keydown', onKeyDown, {
        capture: true,
        passive: false
    });
    return React.createElement("div", { ref: refOuter }, children);
};
export default TabTrap;
